import { useApiQuery } from "../../../services/api";
import { useCurrentUser } from "../../user/hooks/useCurrentUser";
import { isUserOnboarded } from "../../user/utils/isUserOnboarded";
import { usePracticeArm, type PracticeArm } from "./usePracticeArm";

/**
 * Whether this account still owes an answer to the practice question.
 *
 * The mirror of the topics gate: the control arm holds an unprofiled account in
 * front of `/onboarding/interests`, and the practice arm holds an unconfirmed
 * one in front of `/onboarding/practice`. Same place in the funnel, different
 * question — which is what makes the two arms comparable at all. Without it the
 * practice arm targets accounts the server LENDS a practice to, and the
 * confirmation rate measures a guess rather than an answer.
 */
export type PracticeProfilingGate = "pending" | "not-needed" | "needed";

/**
 * What the two reads this gate rests on can say. Unions rather than a value
 * beside a settled flag, because `confirmedAt: null` and "the request has not
 * come back" are the two states that must never be conflated: the first opens
 * the gate, the second must leave the reader alone.
 *
 * `settled` can only mean what it says because of two rules held elsewhere, and
 * both were once broken here. The write RE-READS this key instead of invalidating
 * it (`savePracticeProfiles`, `refetchApiQuery`) — an invalidation could be
 * answered by the GET this hook itself fired the moment the account came into
 * existence, which is the same instant signup writes the answer, leaving a
 * pre-write body in a cache nothing would refresh again. And the key is NOT
 * persisted (`services/queryPersistence.ts`) — enforced on the way out of storage
 * as well as into it, so a body an OLDER bundle wrote cannot be the one this gate
 * decides on either. Without those two, `state === "success"` means
 * "some body exists", which is not an answer — and this gate turns it into an
 * irreversible redirect.
 */
type HeldAnswer =
  | { readonly status: "unsettled" }
  | { readonly status: "settled"; readonly confirmedAt: string | null };

type SpecialtyOffer =
  | { readonly status: "unsettled" }
  | { readonly status: "settled"; readonly count: number };

/** Pure so the rule is assertable without a React harness. */
export function resolvePracticeProfilingGate(input: {
  readonly arm: PracticeArm;
  readonly isOnboarded: boolean;
  readonly held: HeldAnswer;
  readonly offer: SpecialtyOffer;
}): PracticeProfilingGate {
  // NEVER on `pending`. This decides a REDIRECT, the one shape
  // `.knowledge/growthbook-oneshot-flag-race.md` calls out: fired before the
  // payload lands it drops a reader into a screen they can only leave by
  // answering it, and no later re-render takes that back. Out of the arm there
  // is no gate at all.
  if (input.arm !== "in") return "not-needed";
  // Signup asks this on its own slide, and the account it is about does not
  // exist yet.
  if (!input.isOnboarded) return "not-needed";
  // Same care as the arm: an unanswered read is not an answer. An errored one
  // stays here too, so a failing API leaves Explore open rather than sealing it.
  if (input.held.status === "unsettled") return "pending";
  if (input.offer.status === "unsettled") return "pending";
  // They answered — including by unchecking everything, which is why this reads
  // the timestamp and not the practices held.
  if (input.held.confirmedAt !== null) return "not-needed";
  // Nothing to ask. Behind a gate an empty catalogue is a permanent wall: the
  // editor renders a message and no save is possible. The arm already reads
  // this catalogue, but the gate must not rest on that coupling.
  if (input.offer.count === 0) return "not-needed";
  return "needed";
}

/**
 * The gate, for a signed-in reader.
 *
 * Both reads are conditioned on the arm, and both are already issued downstream
 * by `PracticeProfilesEditor`: react-query serves them from one key and one
 * request, the same way `DiscoverySurface` shares the catalogue read.
 */
export function usePracticeProfilingGate(): PracticeProfilingGate {
  const arm = usePracticeArm();
  const { currentUser } = useCurrentUser();
  const specialtyUid =
    currentUser === null || currentUser.mainSpecialtyModel === null
      ? null
      : currentUser.mainSpecialtyModel.uid;

  const heldQuery = useApiQuery(
    "/webapp/private/practice-profiles/me",
    arm === "in" ? {} : false
  );
  const offerQuery = useApiQuery(
    "/webapp/public/practice-profiles/offered",
    arm === "in" && specialtyUid !== null ? { query: { specialtyUid } } : false
  );

  return resolvePracticeProfilingGate({
    arm,
    isOnboarded: currentUser !== null && isUserOnboarded(currentUser),
    held:
      heldQuery.state === "success"
        ? { status: "settled", confirmedAt: heldQuery.body.confirmedAt }
        : { status: "unsettled" },
    // No specialty is no offer, and it is SETTLED rather than pending: nothing
    // is in flight to wait for, and the editor behind the gate would have
    // nothing to show either.
    offer:
      specialtyUid === null
        ? { status: "settled", count: 0 }
        : offerQuery.state === "success"
          ? { status: "settled", count: offerQuery.body.docs.length }
          : { status: "unsettled" },
  });
}
